import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import locationService from '../../services/locationService'
import {
  setDeliver,
  setDeliverType,
  setPickUp,
  setPickUpType,
  setSelectedAddress,
  setSelectedAddressDeliverable,
  setSelectedPoint,
  setSelectedPointDeliverable,
  setSelectedShop,
  setSelectedShopDeliverable,
  setServicefee
} from '../../store/slices/cartSlice'

const useWhereLocation = (mode) => {
  const dispatch = useDispatch()

  const userId = useSelector(state => state.cart.userInfo.selectedUserId)
  const shopId = useSelector(state => state.cart.userInfo.shopId)

  const selectedAddressPickup = useSelector(state => state.cart.userInfo.selectedAddress)
  const selectedPointPickup = useSelector(state => state.cart.userInfo.selectedPoint)
  const selectedShopPickup = useSelector(state => state.cart.userInfo.selectedShop)
  const shopDeliveryFee = useSelector(state => state.cart.userInfo.shopDeliveryFee)

  const selectedAddressDeliver = useSelector(state => state.cart.userInfo.selectedAddressDeliverable)
  const selectedPointDeliver = useSelector(state => state.cart.userInfo.selectedPointDeliverable)
  const selectedShopDeliver = useSelector(state => state.cart.userInfo.selectedShopDeliverable)

  const [isOpenShop, setIsOpenShop] = useState(false)
  const [isOpenAddress, setIsOpenAddress] = useState(false)
  const [isOpenPoint, setIsOpenPoint] = useState(false)
  const [deliveryAddressData, setDeliveryAddressData] = useState([])
  const [collectionPointData, setCollectionPointData] = useState([])
  const [shopData, setShopData] = useState([])
  const [serviceFee, setServiceFee] = useState(null)
  const [deliveryFee, setDeliveryFee] = useState(null)

  const toggleDropdownShop = useCallback(() => setIsOpenShop(prev => !prev), [])
  const toggleDropdownAddress = useCallback(() => setIsOpenAddress(prev => !prev), [])
  const toggleDropdownPoint = useCallback(() => setIsOpenPoint(prev => !prev), [])

  useEffect(() => {
    if (!userId) return
    locationService.fetchDeliveryAddresses(userId).then(setDeliveryAddressData).catch(() => {})
    locationService.fetchCollectionPoints(userId).then(setCollectionPointData).catch(() => {})
  }, [userId])

  useEffect(() => {
    if (!shopId) return
    locationService.fetchShops(shopId).then(setShopData).catch(() => {})
  }, [shopId])

  useEffect(() => {
    const isPickup = mode === 'pickup'
    const hasExplicitSelection = isPickup
      ? (selectedAddressPickup || selectedPointPickup)
      : (selectedAddressDeliver || selectedPointDeliver)
    if (shopId && !hasExplicitSelection) {
      const defaultShop = shopData?.find(shop => shop.id === shopId)
      const defaultShopName = defaultShop ? defaultShop.name_shop : ""
      const defaultShopId = defaultShop ? defaultShop.id : ""
      const defaultShopType = defaultShop ? defaultShop.type : ""
      if (isPickup) {
        dispatch(setSelectedShop(defaultShopName))
        dispatch(setPickUp(defaultShopId))
        dispatch(setPickUpType(defaultShopType))
        dispatch(setSelectedShopDeliverable(defaultShopName))
        dispatch(setDeliver(defaultShopId))
        dispatch(setDeliverType(defaultShopType))
      } else {
        dispatch(setSelectedShopDeliverable(defaultShopName))
        dispatch(setDeliver(defaultShopId))
        dispatch(setDeliverType(defaultShopType))
      }
    }
  }, [mode, shopData, dispatch, shopId, selectedAddressPickup, selectedPointPickup, selectedAddressDeliver, selectedPointDeliver])

  const onSelectShop = useCallback((shop) => {
    const isPickup = mode === 'pickup'
    if (isPickup) {
      dispatch(setSelectedShop(shop.name_shop))
      dispatch(setSelectedPoint(''))
      dispatch(setSelectedAddress(''))
      dispatch(setPickUp(shop.id))
      dispatch(setPickUpType(shop.type))
      dispatch(setSelectedShopDeliverable(shop.name_shop))
      dispatch(setSelectedPointDeliverable(''))
      dispatch(setSelectedAddressDeliverable(''))
      dispatch(setDeliver(shop.id))
      dispatch(setDeliverType(shop.type))
    } else {
      dispatch(setSelectedShopDeliverable(shop.name_shop))
      dispatch(setSelectedPointDeliverable(''))
      dispatch(setSelectedAddressDeliverable(''))
      dispatch(setDeliver(shop.id))
      dispatch(setDeliverType(shop.type))
    }
    setIsOpenShop(false)
  }, [dispatch, mode])

  const onSelectAddress = useCallback((address) => {
    const isPickup = mode === 'pickup'
    const feeToApply = address.delivery_fee
    setDeliveryFee(feeToApply)
    if (isPickup) {
      dispatch(setServicefee(shopDeliveryFee))
      dispatch(setSelectedAddress(address.address))
      dispatch(setSelectedPoint(''))
      dispatch(setSelectedShop(''))
      dispatch(setPickUp(address.id))
      dispatch(setPickUpType(address.type))
      dispatch(setSelectedAddressDeliverable(address.address))
      dispatch(setSelectedShopDeliverable(''))
      dispatch(setSelectedPointDeliverable(''))
      dispatch(setDeliver(address.id))
      dispatch(setDeliverType(address.type))
    } else {
      dispatch(setSelectedAddressDeliverable(address.address))
      dispatch(setSelectedShopDeliverable(''))
      dispatch(setSelectedPointDeliverable(''))
      dispatch(setDeliver(address.id))
      dispatch(setDeliverType(address.type))
    }
    setIsOpenAddress(false)
  }, [dispatch, mode, shopDeliveryFee])

  const onSelectPoint = useCallback((point) => {
    const isPickup = mode === 'pickup'
    setServiceFee(point.service_fee)
    if (isPickup) {
      dispatch(setSelectedPoint(point.collection_points_name))
      dispatch(setSelectedAddress(''))
      dispatch(setServicefee(point.service_fee))
      dispatch(setSelectedShop(''))
      dispatch(setPickUp(point.collection_points_id))
      dispatch(setPickUpType(point.type))
      dispatch(setSelectedPointDeliverable(point.collection_points_name))
      dispatch(setSelectedAddressDeliverable(''))
      dispatch(setSelectedShopDeliverable(''))
      dispatch(setDeliver(point.collection_points_id))
      dispatch(setDeliverType(point.type))
    } else {
      dispatch(setSelectedPointDeliverable(point.collection_points_name))
      dispatch(setSelectedAddressDeliverable(''))
      dispatch(setSelectedShopDeliverable(''))
      dispatch(setDeliver(point.collection_points_id))
      dispatch(setDeliverType(point.type))
    }
    setIsOpenPoint(false)
  }, [dispatch, mode])

  const labels = useMemo(() => {
    if (mode === 'pickup') {
      return {
        selectedShopLabel: selectedShopPickup,
        selectedAddressLabel: selectedAddressPickup,
        selectedPointLabel: selectedPointPickup
      }
    }
    return {
      selectedShopLabel: selectedShopDeliver,
      selectedAddressLabel: selectedAddressDeliver,
      selectedPointLabel: selectedPointDeliver
    }
  }, [mode, selectedShopPickup, selectedAddressPickup, selectedPointPickup, selectedShopDeliver, selectedAddressDeliver, selectedPointDeliver])

  const isNextDisabled = useMemo(() => {
    return !(labels.selectedShopLabel || labels.selectedAddressLabel || labels.selectedPointLabel)
  }, [labels])

  return {
    // data
    deliveryAddressData,
    collectionPointData,
    shopData,
    serviceFee,
    deliveryFee,
    // dropdown state
    isOpenShop,
    isOpenAddress,
    isOpenPoint,
    toggleDropdownShop,
    toggleDropdownAddress,
    toggleDropdownPoint,
    // labels
    ...labels,
    // handlers
    onSelectShop,
    onSelectAddress,
    onSelectPoint,
    // derived
    isNextDisabled
  }
}

export default useWhereLocation



